import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";

import { TokenManager } from "../storage/tokenmanager.storage";
import { API_CONFIG } from "../apiConfig";

const normalizePath = (path: string): string => {
  return path.replace(/\/+/g, "/");
};

const isAuthEndpoint = (url: string): boolean => {
  const authEndpoints = [
    "/api/auth/register",
    "/api/auth/login",
    "/api/auth/logout",
    "/api/auth/refresh",
    "/api/auth/forgot-password",
    "/api/auth/reset-password",
    "/api/auth/verify-email",
    "/api/auth/resend-verification",
    "/api/auth/verify-reset-code",
    "/api/auth/change-password",
  ];

  try {
    const urlObj = new URL(url, window.location.origin);
    const normalizedPath = normalizePath(urlObj.pathname);
    return authEndpoints.some((endpoint) => normalizedPath === endpoint);
  } catch {
    return false;
  }
};

const isPublicEndpoint = (url: string, method: string = "GET"): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);
    const path = normalizePath(urlObj.pathname);

    if (method === "GET") {
      if (path === "/api/products" || path.startsWith("/api/products?")) {
        return true;
      }

      const productDetailPath =
        path.match(/^\/api\/products\/[^/]+$/) ||
        path.match(/^\/products\/[^/]+$/);
      if (
        productDetailPath &&
        !path.includes("/save") &&
        !path.includes("/saved") &&
        !path.includes("/view") &&
        !path.includes("/my-products")
      ) {
        return true;
      }

      if (path === "/api/categories" || path.startsWith("/api/categories/")) {
        return true;
      }

      if (path === "/api/requests" || path.startsWith("/api/requests?")) {
        return true;
      }
    }

    const alwaysPublicEndpoints: string[] = [];

    if (
      alwaysPublicEndpoints.some(
        (endpoint) => path === endpoint || path.startsWith(endpoint + "/"),
      )
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

const isPublicPage = (): boolean => {
  const pathname = window.location.pathname;
  const publicPaths = ["/", "/home", "/login", "/register", "/forgot-password", "/reset-password", "/profile-setup", "/verify-email"];
  if (publicPaths.includes(pathname)) return true;

  const isProductDetailPage = /^\/product\/[^/]+$/.test(pathname);
  return isProductDetailPage;
};

const redirectToLogin = (): void => {
  const currentPath = window.location.pathname + window.location.search;

  // The session is invalid. Drop the tokens synchronously BEFORE navigating,
  // otherwise route guards (GuestRoute) still consider the user authenticated
  // and bounce them straight back to the protected page, which 401s again and
  // forces another full page reload — an endless reload/redirect loop.
  try {
    localStorage.removeItem(API_CONFIG.TOKEN_STORAGE_KEY);
    localStorage.removeItem(API_CONFIG.REFRESH_TOKEN_KEY);
  } catch {
    // Ignore storage failures (e.g. privacy mode); the redirect still proceeds.
  }

  if (!currentPath.includes("/login")) {
    localStorage.setItem("redirectAfterLogin", currentPath);
  }
  window.location.href = `/login?message=session_expired&redirect=${encodeURIComponent(
    currentPath,
  )}`;
};

export class ApiError extends Error {
  public statusCode: number;
  public response?: AxiosResponse;
  constructor(statusCode: number, message: string, response?: AxiosResponse) {
    super(message);
    this.name = "ApiError";
    this.message = message;
    this.statusCode = statusCode;
    this.response = response;
  }
}

class ApiService {
  private static instance: ApiService;
  private axiosInstance: AxiosInstance;
  private baseURL: string;

  private constructor(baseURL: string) {
    this.baseURL = baseURL.replace(/\/+$/, "");
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
    });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor: attach auth header and handle public/auth endpoints
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        config.headers = (config.headers || {}) as any;
        config.headers["Accept"] = "application/json";

        const method = (config.method || "GET").toUpperCase();

        const requestUrl = (() => {
          const urlPath = (config.url || "").toString();

          // Ensure we keep the full baseURL path (e.g. `/api`) when
          // building the absolute URL used for classification.
          if (!this.baseURL) {
            return urlPath;
          }

          const base = this.baseURL.replace(/\/+$/, "");

          if (urlPath.startsWith("/")) {
            // `/notifications` + `http://localhost:3001/api`
            // -> `http://localhost:3001/api/notifications`
            return `${base}${urlPath}`;
          }

          // `notifications` + `http://localhost:3001/api`
          // -> `http://localhost:3001/api/notifications`
          return `${base}/${urlPath}`;
        })();

        const isOurApi =
          !this.baseURL || requestUrl.includes(this.baseURL || "");
        const authEndpoint = isAuthEndpoint(requestUrl);
        const publicEndpoint = isPublicEndpoint(requestUrl, method);
        const shouldSkip = authEndpoint || publicEndpoint;

        if (shouldSkip) {
          return config;
        }

        if (!isOurApi && !config.url?.startsWith("/")) {
          return config;
        }

        if ((TokenManager as any).isVisitor?.()) {
          return config;
        }

        let token = TokenManager.getAccessToken();
        let isExpired = token
          ? (TokenManager as any).isTokenExpired?.(token) ?? false
          : true;

        if (isPublicPage() && (!token || isExpired)) {
          return config;
        }
        if (token) {
          config.headers = config.headers || {};
          (config.headers as any)["Authorization"] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor: handle 401 with token refresh + retry
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Check if error response indicates a deactivated/suspended account
        let isDeactivationError = false;
        if (error.response) {
          const data = error.response.data as { message?: string; error?: string } | null;
          const msg = data?.message || data?.error || '';
          const lowerMsg = String(msg).toLowerCase();
          if (
            lowerMsg.includes('deactivat') ||
            lowerMsg.includes('suspended') ||
            lowerMsg.includes('inactive')
          ) {
            isDeactivationError = true;
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('user:deactivated', { detail: { message: msg } })
              );
            }, 0);
          }
        }

        // If 401 and we haven't retried yet and we have a refresh token
        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          TokenManager.getRefreshToken()
        ) {
          // When account is deactivated, skip refresh — it will fail
          if (isDeactivationError) {
            TokenManager.clearTokens();
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const newToken = await TokenManager.refreshToken();
            // Update the header and retry
            originalRequest.headers = originalRequest.headers || {};
            (originalRequest.headers as any)["Authorization"] = `Bearer ${newToken}`;
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            // Refresh failed — session is invalid, force logout and redirect
            console.error('Token refresh failed in interceptor:', refreshError);
            if (!isPublicPage()) {
              if (!isDeactivationError) {
                redirectToLogin();
              }
            }
            return Promise.reject(refreshError);
          }
        }

        if (error.response?.status === 401 && !isPublicPage()) {
          // Check if this is a repeated failure
          const failureCount = (error.config as any)._failureCount || 0;
          (error.config as any)._failureCount = failureCount + 1;

          // If failed multiple times, clear state and redirect
          if (failureCount >= 2) {
            console.warn('Multiple 401 failures, clearing auth state');
            TokenManager.clearTokens();
            if (!isDeactivationError) {
              redirectToLogin();
            }
          } else if (!originalRequest?._retry) {
            if (!isDeactivationError) {
              redirectToLogin();
            }
          }
        }

        // Non-401 errors or already retried
        if (error.response) {
          const data = error.response.data as { message?: string; error?: string } | null;
          let message = data?.message || data?.error || "An error occurred";
          if (error.response.status === 403) {
            message = `${message} Please contact support for help.`;
          }
          throw new ApiError(
            error.response.status,
            message,
            error.response,
          );
        } else {
          throw new ApiError(500, error.message);
        }
      },
    );
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      const url = (process.env.REACT_APP_API_URL || API_CONFIG.BASE_URL).replace(/\/$/, "");
      ApiService.instance = new ApiService(url);
    }
    return ApiService.instance;
  }

  public removeHeader(): void {
    this.axiosInstance.defaults.headers.common = {};
  }

  public get<T>(
    resource: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get(resource, options);
  }

  public post<T>(
    resource: string,
    data: Record<string, unknown> | unknown,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post(resource, data, options);
  }

  public patch<T>(
    resource: string,
    data: Record<string, unknown> | unknown,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.patch(resource, data, options);
  }

  public put<T>(
    resource: string,
    data: Record<string, unknown> | unknown,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put(resource, data, options);
  }

  public delete<T>(
    resource: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete(resource, options);
  }
}

export const apiService = ApiService.getInstance();
